<!--
There are several different presets for this component.
Either a preset must be selected via the "version" attribute, or all of the
following attributes must be provided:
- button-text
- button-link
- image-url

Example: <image-cta-large version='heat-pump'></image-cta-large>

The possible values for `version` are:
- 'rooftop-solar'
- 'heat-pump'
- 'car-charging'
- 'car-outside'
- 'showroom'

Presets may define the `headline` text, `body` html, `button` text, `link` URL,
and `image` component.
To see the values for each preset, look at the contents of `data()` in the
script section of this file.
-->

<template>
    <div
        class="image-cta-large"
        :style="extraStyle"
    >
        <div
            class="
                d-flex flex-column flex-sm-row bg-white border border-gray-200
                align-items-center rounded card-shadow card-width text-sans
            "
        >
            <!-- Image section -->
            <div
                class="image-container flex-shrink-0"
                :class="imageRight ? 'order-sm-2' : 'order-sm-0'"
            >
                <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    class="anchor-image"
                >
                <component
                    :is="presets[version].image"
                    v-else-if="version && presets[version].image"
                />
            </div> <!-- Image section -->

            <!-- Text & form section -->
            <div
                class="d-flex flex-column flex-grow-1 p-3 px-sm-4 justify-content-center text-center order-1"
            >
                <h5
                    class="mx-3 mb-3 mb-lg-4 text-serif"
                >
                    <span v-if="headline">
                        {{ headline }}
                    </span>
                    <span v-else-if="version && presets[version].headline">
                        {{ presets[version].headline }}
                    </span>
                    <slot v-else>
                        Find out what solar panels cost in your area in {{ currentYear }}
                    </slot>
                </h5>

                <!-- Optional body text -->
                <div
                    v-if="bodyHtml || version && presets[version].body"
                    class="mb-2"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        v-if="bodyHtml"
                        v-html="bodyHtml"
                    />
                    <span
                        v-else
                        v-html="presets[version].body"
                    />
                </div> <!-- Optional body text -->

                <form
                    ref="ctaForm"
                    method="GET"
                    class="mb-0 text-left"
                    :action="buttonLink || presets[version].link"
                    @submit.prevent="validateAndSubmit"
                >
                    <div v-if="rc !== ''">
                        <input
                            name="rc"
                            type="hidden"
                            :value="rc"
                        >
                    </div>

                    <!-- Zip entry section -->
                    <div
                        v-if="zipEntry"
                        class="d-block"
                    >
                        <div class="zip-entry-wrapper">
                            <es-input
                                :id="zipCodeInputId"
                                v-model="zipCode"
                                name="zip_code"
                                input-class="zip-entry bg-gray-100 border-gray-500"
                                :validator="$v.zipCode"
                                placeholder="5-digit zip code"
                                label="Enter your zip code"
                                bold-label
                            >
                                <template #afterInput>
                                    <location-icon
                                        class="location-icon"
                                        aria-hidden="true"
                                    />
                                </template>
                                <template #validationText>
                                    This field is required
                                </template>
                            </es-input>
                        </div>
                    </div> <!-- Zip entry section -->

                    <div>
                        <es-button
                            class="w-100"
                            type="submit"
                        >
                            <span v-if="buttonText">
                                {{ buttonText }}
                            </span>
                            <span v-else>
                                {{ presets[version].button }}
                            </span>
                        </es-button>
                    </div>
                </form>
            </div> <!-- Text & form section -->
        </div>
    </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { validationMixin } from '@/utils/mixins';
import { makeIdUnique } from '@/utils/functions';
import EsButton from '@/components/EsButton';
import EsInput from '@/components/EsInput';
import LocationIcon from '@/components/icons/LocationIcon';
import BlogImage from '@/components/images/Blog';
import CarImage from '@/components/images/Car';
import CarOutsideImage from '@/components/images/CarOutside';
import HeatPumpImage from '@/components/images/HeatPump';
import ShowroomImage from '@/components/images/Showroom';

export default {
    components: {
        EsButton,
        EsInput,
        LocationIcon,
        BlogImage,
        CarImage,
        CarOutsideImage,
        HeatPumpImage,
        ShowroomImage,
    },
    mixins: [validationMixin],
    props: {
        headline: {
            type: String,
            required: false,
            default: '',
        },
        bodyHtml: {
            type: String,
            required: false,
            default: '',
        },
        buttonText: {
            type: String,
            required: false,
            default: '',
        },
        buttonLink: {
            type: String,
            required: false,
            default: '',
        },
        version: {
            type: String,
            required: false,
            default: '',
        },
        imageUrl: {
            type: String,
            required: false,
            default: '',
        },
        rc: {
            type: String,
            required: false,
            default: '',
        },
        extraStyle: {
            type: String,
            required: false,
            default: '',
        },
        zipEntry: {
            type: Boolean,
            required: false,
            default: false,
        },
        imageRight: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            zipCode: '',
            zipCodeInputId: makeIdUnique('zip_code'),
            /* eslint-disable max-len */
            presets: {
                'rooftop-solar': {
                    button: 'Get Started',
                    link: 'https://www.energysage.com/market/start/',
                    image: BlogImage,
                },
                'car-charging': {
                    button: 'Learn More',
                    link: 'https://electricvehicles.energysage.com/',
                    image: CarImage,
                },
                'car-outside': {
                    button: 'Learn More',
                    link: 'https://electricvehicles.energysage.com/',
                    image: CarOutsideImage,
                },
                showroom: {
                    button: 'Learn More',
                    link: 'https://electricvehicles.energysage.com/',
                    image: ShowroomImage,
                },
            },
        };
    },
    computed: {
        currentYear() {
            return new Date().getFullYear();
        },
    },
    methods: {
        validateAndSubmit(event) {
            this.$v.$touch();
            if (this.zipEntry && this.$v.$error) {
                return;
            }
            // TODO: this is carried over from a specific use case, but in the
            // future may be adapted to have custom tracking for various CTAs
            if (window.gaTrackingView === 'Summary') {
                window.gaEvent('summary_cta_referral_link', 'click', 'referral_link');
            }
            event.target.submit();
        },
    },
    validations: {
        zipCode: {
            required,
            minLength: minLength(5),
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/bs4/_helpers.scss";

.image-cta-large {
    :deep() {
        @import "@/sass/widgets.scss";
        font-size: $font-size-base;
    }
}

.card-width {
    max-width: 315px;
    @include media-breakpoint-up(sm) {
        max-width: 700px;
    }
}

:deep(.zip-entry) {
    font-family: $font-family-sans-serif!important;
    font-size: 16px!important;
}

.card-shadow {
    box-shadow: 4px 5px 8px 0 rgba(86, 86, 86, 0.17);
}

.location-icon {
    position: absolute;
    right: 8px;
}

.image-container {
    width: 315px;
    height: 275px;
    @include media-breakpoint-up(sm) {
        width: 266px;
        height: 228px;
    }
    @include media-breakpoint-up(md) {
        width: 350px;
        height: 300px;
    }
}
.anchor-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    @include media-breakpoint-up(sm) {
        min-width: 266px;
        height: 228px;
    }
    @include media-breakpoint-up(md) {
        min-width: 350px;
        height: 300px;
    }
}

.zip-entry-wrapper .form-group {
    margin-bottom: .5rem;
}
</style>
